// @flow
import { PlusCircle, TrashSimple } from '@phosphor-icons/react'
import { Button } from 'antd'
import _ from 'lodash'
import * as React from 'react'
import type { AnswerWithRepeatable } from 'types/answers/answers'
import type { Page } from 'types/page'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import { hasText } from 'utils/helpers'
import { v4 as uuid } from 'uuid'

import cn from '@/utils/tailwind/cn'

import FormSection from './FormSection'
import RepeatableSectionWrapper from './RepeatableSectionWrapper'
import ZentakeFormContext from './ZentakeFormContext'

type Props = {
  page: Page,
  hidden?: boolean,
  response: Response,
  sessions?: SessionObject,
  answers: AnswerWithRepeatable[],
  allPages?: Page[],
  isChildPage?: boolean,
}

function FormPage({
  page,
  hidden,
  response,
  sessions,
  answers,
  allPages = [],
  isChildPage,
}: Props): React.Node {
  const formContext = React.useContext(ZentakeFormContext)
  const isRepeatableSection = Boolean(page?.repeat_section)
  const isReadOnlyMode = formContext?.mode === 'read-only'

  const [repeatableSections, setRepeatableSections] = React.useState([])
  const lastUpdateRef = React.useRef(null)

  const childPages = React.useMemo(
    () =>
      allPages.filter((currentPage) => currentPage.parent_page_id === page.id),
    [allPages, page.id]
  )

  const hasChildPages = React.useMemo(() => childPages.length, [childPages])

  const addRepeatableSection = React.useCallback(() => {
    const newSection = {
      page_id: uuid(),
      original_page_id: page.id,
      order: repeatableSections.length + 1,
      answers: [],
    }
    setRepeatableSections((prev) => [...prev, newSection])
  }, [page.id, repeatableSections.length])

  const removeRepeatableSection = React.useCallback((index) => {
    setRepeatableSections((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const updateRepeatableAnswer = React.useCallback(
    (sectionId, questionId, value) => {
      setRepeatableSections((prev) => {
        const updated = prev.map((section) => {
          if (section.page_id === sectionId) {
            const currentAnswers = section.answers || []
            const existingAnswerIndex = currentAnswers.findIndex(
              (a) => String(a.question) === String(questionId)
            )
            const stringValue = value ? String(value) : ''

            if (existingAnswerIndex >= 0) {
              const existingAnswer = currentAnswers[existingAnswerIndex]
              if (existingAnswer.answer_raw === stringValue) {
                return section
              }

              const updatedAnswers = [...currentAnswers]
              updatedAnswers[existingAnswerIndex] = {
                ...updatedAnswers[existingAnswerIndex],
                answer_raw: stringValue,
              }
              return {
                ...section,
                answers: updatedAnswers,
              }
            } else if (stringValue !== '') {
              const newAnswer = {
                id: uuid(),
                question: Number(questionId),
                answer_raw: stringValue,
                order: currentAnswers.length + 1,
              }
              return {
                ...section,
                answers: [...currentAnswers, newAnswer],
              }
            }
          }
          return section
        })
        return updated
      })
    },
    []
  )

  const updateSaveAnswerRepeatSection = React.useCallback(() => {
    if (!formContext?.setResponse) return

    const currentSectionsData = repeatableSections.map((section) => ({
      page_id: section.page_id,
      original_page_id: section.original_page_id,
      order: section.order,
      answers: section.answers || [],
    }))

    const currentDataString = JSON.stringify(currentSectionsData)

    if (lastUpdateRef.current === currentDataString) {
      return
    }

    lastUpdateRef.current = currentDataString

    if (!page.parent_page_id) {
      formContext.setResponse((prev) => ({
        ...prev,
        save_answer_repeat_section: currentSectionsData,
      }))
    } else {
      formContext.setResponse((prev) => {
        const existingSections = prev.save_answer_repeat_section || []
        const filteredSections = existingSections.filter(
          (section) => section.original_page_id !== page.id
        )

        return {
          ...prev,
          save_answer_repeat_section: [
            ...filteredSections,
            ...currentSectionsData,
          ],
        }
      })
    }
  }, [repeatableSections, formContext, page.id, page.parent_page_id])

  const getRepeatableAnswers = React.useCallback(
    (sectionId) => {
      const section = repeatableSections.find((s) => s.page_id === sectionId)
      return section?.answers || []
    },
    [repeatableSections]
  )

  React.useEffect(() => {
    updateSaveAnswerRepeatSection()
  }, [updateSaveAnswerRepeatSection])

  React.useEffect(() => {
    if (!isRepeatableSection || !answers.length) return
    const repeatableSections = answers
      ?.filter((item) => item.original_page_id === page.id)
      ?.sort((a, b) => a.order - b.order)
    setRepeatableSections(repeatableSections)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id, isRepeatableSection, page])

  return (
    <div
      className={cn(
        'form-page mb-4 self-center rounded border border-gray-400 bg-white pb-4 sm:mx-6 sm:pb-6',
        { 'mb-0 !border-none pb-0': isRepeatableSection || isChildPage },
        { '!mx-0': isChildPage && !isRepeatableSection },
        { 'pt-2': isRepeatableSection }
      )}
      hidden={hidden}
    >
      {hasText(page?.title) && !isRepeatableSection && !isChildPage && (
        <div className="px-4 pt-4 sm:pt-6">
          <p className="text-3xl text-gray-700">{page.title}</p>
        </div>
      )}

      {_.orderBy(page.sections, ['order'], ['asc']).map((s, index) => (
        <div
          className={cn('mt-3 flex flex-col', {
            '!mt-0': isChildPage && !isRepeatableSection,
          })}
          key={`original-${s.id}`}
        >
          {isRepeatableSection ? (
            <h2 className="relative w-full rounded-t border-l border-r border-t !border-gray-200/50 bg-gray-100/70 px-4 py-3 font-medium">
              Item 1
            </h2>
          ) : null}
          <FormSection
            className={cn(
              isRepeatableSection &&
                'rounded !rounded-t-none border !border-t-0 !border-gray-200/70 pb-4'
            )}
            isLast={index === page.sections.length - 1}
            isRepeatableSection={isRepeatableSection}
            page={page}
            response={response}
            section={s}
            sessions={sessions}
          />
        </div>
      ))}

      {hasChildPages
        ? childPages.map((childPage) => (
            <FormPage
              isChildPage
              allPages={allPages}
              answers={answers}
              key={childPage.id}
              page={childPage}
              response={response}
              sessions={sessions}
            />
          ))
        : null}

      {isRepeatableSection
        ? repeatableSections?.map((repeatableSection, sectionIndex) => {
            const sectionAnswers = getRepeatableAnswers(
              repeatableSection.page_id
            )

            return (
              <RepeatableSectionWrapper
                key={`repeatable-${repeatableSection.page_id}`}
                originalPageId={repeatableSection.original_page_id}
                sectionAnswers={sectionAnswers}
                sectionId={repeatableSection.page_id}
                sectionOrder={repeatableSection.order}
                updateRepeatableAnswer={updateRepeatableAnswer}
              >
                <div className="mt-3 flex flex-col">
                  <h2 className="relative w-full rounded-t border-l border-r border-t !border-gray-200/50 bg-gray-100/70 px-4 py-3 font-medium">
                    Item {sectionIndex + 2}
                    {!isReadOnlyMode && (
                      <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-gray-700 hover:text-red-600"
                        onClick={() => removeRepeatableSection(sectionIndex)}
                        type="button"
                      >
                        <TrashSimple className="text-xl" weight="bold" />
                      </button>
                    )}
                  </h2>
                  {_.orderBy(page.sections, ['order'], ['asc']).map(
                    (s, index) => (
                      <FormSection
                        isRepeatableSection
                        className="rounded !rounded-t-none border !border-t-0 !border-gray-200/70 pb-4"
                        isLast={index === page.sections.length - 1}
                        key={`repeatable-${repeatableSection.page_id}-${s.id}`}
                        page={page}
                        response={response}
                        section={s}
                        sessions={sessions}
                      />
                    )
                  )}
                </div>
              </RepeatableSectionWrapper>
            )
          })
        : null}

      {isRepeatableSection && !isReadOnlyMode ? (
        <div className="mt-4 flex">
          <Button
            icon={<PlusCircle className="text-xl" weight="bold" />}
            onClick={addRepeatableSection}
            type="primary"
          >
            Add Item
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export default FormPage
